// src/pages/oura-workspace/index.tsx
//
// One route element for every Oura single-business screen (plus the staff
// pipeline and the frozen report). It resolves the business in focus — from the
// URL for staff, or the signed-in client's own business — hands the ported
// screen its data, and translates the prototype's onNavigate/onSelectBusiness
// callbacks into router pushes. The screens themselves are unchanged ports.

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { AlertTriangleIcon, ArrowLeftIcon, Loader2Icon, SparklesIcon } from "lucide-react";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import OuraShell from "@container/layout/oura-shell";
import { ouraHref, OURA_ROOT, OURA_TEAM_ACCESS_PATH, isBusinessSidebarLive, type View } from "@oura/nav";
import { useOuraRole, isOuraRole } from "@oura/role";
import { useUserProfileContext } from "@context/user_profile";
import { useSignedInName } from "@oura/docs/signedIn";
import { getBusiness, getSnapshot } from "@oura/data";
import { getConnectors } from "@oura/connectors";
import { deriveGscState, findGscConnector } from "@oura/gscState";
import {
    hasLiveResults,
    adaptBusinessAudit,
    adaptTrendBreak,
    adaptConnectors,
    adaptSearchPerformance,
    adaptReportMetrics,
    liveHeadline,
    adaptTracker,
    buildLiveSnapshot,
    buildLiveBusiness,
    adaptGrounding,
} from "@oura/liveAudit";
import { listBusinesses, type AeoBusiness, getBusinessDetail, getBusinessConnectors, getTracker, patchTrackerTask, patchTrackerSettings, rebuildTracker, decideSuggestion, getScoreHistory, getAssessment, type AeoAssessment, type AeoBusinessDetailResult, type AeoConnector, type AeoScoreHistoryEntry } from "@service/aeo";
import { ActionDrawerProvider } from "@oura/components/ActionDrawer";
import { catchErrorLog } from "@util/sentry";
import { DataConnectors } from "@oura/components/DataConnectors";
import { GatedReason } from "@oura/components/GatedReason";
import { Card, CardBody } from "@oura/primitives/Card";
import { EmptyState } from "@oura/primitives/EmptyState";
import { Button } from "@oura/primitives/Button";
import { Dashboard } from "@oura/screens/Dashboard";
import { Visibility } from "@oura/screens/Visibility";
import { visibilityFixture } from "@oura/visibility/fixtures";
import { SearchPerformance } from "@oura/screens/SearchPerformance";
import { ClientSite } from "@oura/screens/ClientSite";
import { ToDo } from "@oura/screens/ToDo";
import { Businesses } from "@oura/screens/Businesses";
import { Report } from "@oura/screens/Report";
import type { BusinessTracker } from "@oura/tracker";
import { Overview } from "@oura/screens/Overview";
import { BusinessDetail, type DetailTab } from "@oura/screens/BusinessDetail";
import { PastScanBanner, PastScanBar, ScanNotFoundBanner, ScanVersionPicker } from "@oura/components/ScanVersions";
import { ActivityLog } from "@oura/components/ActivityLog";
import { PagesWorkshop } from "@oura/screens/PagesWorkshop";
import { BusinessOverview } from "@oura/screens/BusinessOverview";
import { SiteAudit } from "@oura/screens/SiteAudit";
import { Tracker } from "@oura/screens/Tracker";
import { PlanUnreadable } from "@oura/components/fixes/PlanUnreadable";
import { ClientReviews } from "@oura/screens/ClientReviews";
import { Knowledge } from "@oura/screens/Knowledge";
import { ClientDocuments } from "@oura/screens/ClientDocuments";
import { DocumentsTabs } from "@oura/components/documents/DocumentsTabs";
import { publishableTypeLabel } from "@oura/connectors";
import { DEMO_STAFF } from "@service/aeo/clientReview";
import { ContractTab } from "@oura/screens/ContractTab";
import { Delivery } from "@oura/screens/Delivery";
import { Skills } from "@oura/screens/Skills";
import { FormAnswers } from "@oura/screens/FormAnswers";
import { ClientRequests } from "@oura/screens/ClientRequests";
import { DeliveryBoard } from "@oura/screens/DeliveryBoard";
import { contractChecks } from "@oura/components/plan/planModel";
import { TimelineTab } from "@oura/screens/TimelineTab";
import { ClientPage } from "@oura/screens/ClientPage";
import { merchantReading, nextAiCheckDate } from "@oura/merchant/model";
import { visibilityInputsFromAssessment } from "@oura/visibility/fromLive";
import { getMyBusiness, getClientPage, invalidateClientPage, subscribeClientPage, type ClientPageResult } from "@service/aeo/clientReview";
import { getSearchQuestions as devMockSearchQuestions, getSearchPerformance as devMockSearchPerformance } from "@service/aeo/devMock";
import { getAudit } from "@oura/audit";
import { getTracker as getFixtureTracker } from "@oura/tracker";
import { AeoAssessmentScreen } from "@oura/screens/AeoAssessment";
import { OnboardingTab } from "@oura/screens/OnboardingTab";
import { OnboardingStrip } from "@oura/components/onboarding/OnboardingStrip";
import { factsFromDetail, factsFromRow, rescanInfoFrom, type HubFacts } from "@oura/overview/hubStage";
import { HubEmptyState, HubNextSlot } from "@oura/overview/HubEmpty";
import { SCAN_DRAFT_UI_ON, scanDraftMode } from "@oura/newscan/flags";
import { BRAND_WAIT_LINE, useWaitsForBrand } from "@oura/merchant/brandWait";
import { Access } from "@oura/merchant/Access";
import { SetupBanner } from "@oura/merchant/SetupBanner";
import { MerchantTour } from "@oura/merchant/Tour";
import { MERCHANT_V2 } from "@oura/merchantV2";
import { FirstCheckBlank } from "@oura/merchant/FirstRun";
import { FirstResultsBanner } from "@oura/components/onboarding/FirstResultsBanner";
import { useFirstRunRead } from "@oura/merchant/firstRunState";
import { showOnboarding } from "@oura/nav";

/** /oura → the signed-in role's home (staff pipeline, or client dashboard). */
export function OuraHomeRedirect() {
    const role = useOuraRole();
    return (
        <Navigate
            to={role === "oura-staff" ? `${OURA_ROOT}/businesses` : `${OURA_ROOT}/dashboard`}
            replace
        />
    );
}

/**
 * The client page could not be read. Says which wall was hit, because the two
 * likely ones need opposite actions: a staffer previewing needs a merchant
 * login, a real merchant needs us to look at their account.
 */
function ClientPageError({
    reason,
    previewing,
    onRetry,
}: {
    reason: "forbidden" | "notFound" | "unauthorised" | "network";
    previewing: boolean;
    onRetry: () => void;
}) {
    if (previewing && (reason === "forbidden" || reason === "notFound")) {
        return (
            <Card>
                <CardBody>
                    <EmptyState
                        icon={AlertTriangleIcon}
                        title="You are previewing as a merchant, but the API sees your staff login"
                        description="These screens read the merchant's own data through their token, and that route refuses a staff one. To see the real thing, create a merchant login on the business (Client, Create merchant login) and sign in as that user."
                        action={<Button variant="outline" onClick={onRetry}>Try again</Button>}
                    />
                </CardBody>
            </Card>
        );
    }
    const copy = {
        forbidden: { title: "This account cannot open the merchant view", description: "The login you are signed in with is not a merchant on any business. Ask ourteam to check which account should hold this." },
        notFound: { title: "No business on this login yet", description: "Your account is not linked to a business. Ask ourteam to finish setting it up." },
        unauthorised: { title: "Your session has expired", description: "Sign in again to pick up where you left off." },
        network: { title: "Couldn't reach the server", description: "Nothing is lost. Try again, and tell ourteam if it keeps happening." },
    }[reason];
    return (
        <Card>
            <CardBody>
                <EmptyState
                    icon={AlertTriangleIcon}
                    title={copy.title}
                    description={copy.description}
                    action={<Button onClick={reason === "unauthorised" ? () => window.location.reload() : onRetry}>{reason === "unauthorised" ? "Reload" : "Try again"}</Button>}
                />
            </CardBody>
        </Card>
    );
}

export default function OuraWorkspace({ screen }: { screen: View }) {
    const role = useOuraRole();
    // A new merchant's Visibility says what is coming until the first AI check is ready.
    const { run: firstRun, pending: firstRunPending } = useFirstRunRead(MERCHANT_V2 && role === "oura-client" && screen === "questions");
    const navigate = useNavigate();
    const params = useParams<{ businessId?: string }>();
    // On the dev server, a sample write records the signed-in person, as the server will from the token.
    useSignedInName();
    // The dev role switch changes what the FRONTEND thinks you are; the API
    // reads your token. So a staffer previewing as a merchant is still a
    // staffer to `/businesses/mine/...`, which is 403 for staff by design.
    // Worth saying out loud rather than showing a bare failure.
    const [, userInfo] = useUserProfileContext();
    const previewingAsClient = role === "oura-client" && !isOuraRole(userInfo?.role);

    // A client's business comes from their login (GET /businesses/mine), never
    // from the URL. Undefined while it loads; null when the workspace owns none.
    const [myBusinessId, setMyBusinessId] = useState<string | null | undefined>(undefined);
    useEffect(() => {
        if (role === "oura-staff") return;
        let cancelled = false;
        void getMyBusiness().then((b) => { if (!cancelled) setMyBusinessId(b?.id ?? null); }).catch(() => { if (!cancelled) setMyBusinessId(null); });
        return () => { cancelled = true; };
    }, [role]);
    const businessId =
        role === "oura-staff" ? params.businessId ?? null : myBusinessId ?? null;

    // The client approval layer's payload (packs, threads, plan, numbers).
    // Fetched for the client role only; staff read the same records through
    // the Client tab on the business.
    // undefined = still asking. A result carries its own ok/not-ok, so a failed
    // read can be rendered as a failure instead of an endless spinner.
    const [clientPage, setClientPage] = useState<ClientPageResult | undefined>(undefined);
    // Every read of the page takes a number, and only the newest may set it: a
    // slow older read (a documents poll started before an answer) must never
    // land over the read made after the answer and put the old state back.
    const pageReadSeq = useRef(0);
    const refreshClientPage = useCallback(async (fresh = false) => {
        if (role === "oura-staff") return;
        // The views call this after a decision, a message or a call request, so
        // it must go past the shared read's short cache — otherwise a merchant
        // approves something and the page shows the answer from five seconds
        // ago. The mount below is happy with the shared copy.
        if (fresh) invalidateClientPage();
        const mine = ++pageReadSeq.current;
        const r = await getClientPage();
        // As below: a failed re-read keeps the last good page, so a write that
        // went through never turns the page into its error.
        if (mine === pageReadSeq.current) setClientPage((prev) => (r.ok || prev === undefined ? r : prev));
    }, [role]);
    const refreshClientPageFresh = useCallback(() => refreshClientPage(true), [refreshClientPage]);
    useEffect(() => { void refreshClientPage(); }, [refreshClientPage]);
    // Every re-read anyone asks for (the documents timer, a docs event, the
    // tab coming back) replaces the whole page here, so the sentences, the
    // cards and "Start answering" count from the same read as the nav badge.
    // A failed re-read keeps the last good page on screen.
    useEffect(() => {
        if (role === "oura-staff") return;
        let cancelled = false;
        const off = subscribeClientPage(() => {
            const mine = ++pageReadSeq.current;
            getClientPage().then(
                (r) => {
                    if (cancelled || mine !== pageReadSeq.current) return;
                    // A failed re-read keeps the last good page; it only shows when there is none yet.
                    setClientPage((prev) => (r.ok || prev === undefined ? r : prev));
                },
                () => undefined,
            );
        });
        return () => {
            cancelled = true;
            off();
        };
    }, [role]);
    // An Online client logs in before any AI check (§2.4a): its first check waits for brand checks.
    const waitsForBrand = useWaitsForBrand(role !== "oura-staff" && showOnboarding);
    const [searchParams, setSearchParams] = useSearchParams();
    const focusItemId = searchParams.get("item");
    /**
     * Which scan is on screen (#3933). Absent = the latest, which is what this
     * workspace has always shown.
     *
     * In the URL on purpose: a past scan survives a reload and can be sent to
     * someone. It is also the only state here that changes what every tab
     * means, so hiding it in a component would be hiding the caption of the
     * whole screen.
     */
    const selectedScanId = searchParams.get("scan");
    const isFixtureBusiness = businessId ? !!getBusiness(businessId) : false;

    // Real business data (ourteam-frontend#3160). Fetched unconditionally, ahead
    // of every early return below, so hook order stays stable across renders —
    // the effect itself is a no-op for a fixture id or no id at all.
    // undefined = still loading. Otherwise the fetch RESULT, which distinguishes
    // "this business does not exist" from "we could not ask" — a transient 5xx or
    // an expired session used to be indistinguishable from a 404 here, so the
    // user was redirected away from a business that exists and the real error was
    // never shown.
    const [liveDetail, setLiveDetail] = useState<AeoBusinessDetailResult | undefined>(undefined);
    /** Every terminal scan of this business, newest first (#3933). */
    const [scanHistory, setScanHistory] = useState<AeoScoreHistoryEntry[]>([]);
    /** The past scan being viewed. `"missing"` is an id this login cannot open:
     *  another business's scan, or one that no longer exists. */
    const [pastScan, setPastScan] = useState<AeoAssessment | "loading" | "missing" | null>(null);
    const [liveConnectors, setLiveConnectors] = useState<AeoConnector[]>([]);
    /**
     * A sample business's row on the Businesses list, so its stage is read from
     * the same row the list reads (one next step on both). Undefined while
     * read; null when the list has no row for it.
     */
    const [fixtureRow, setFixtureRow] = useState<AeoBusiness | null | undefined>(undefined);
    useEffect(() => {
        setFixtureRow(undefined);
        if (!businessId || !isFixtureBusiness || !SCAN_DRAFT_UI_ON) return;
        let cancelled = false;
        void listBusinesses().then((rows) => { if (!cancelled) setFixtureRow(rows.find((b) => b.id === businessId) ?? null); });
        return () => { cancelled = true; };
    }, [businessId, isFixtureBusiness]);
    // Bumped on every business switch, so a refresh started against business A
    // that resolves after navigating to business B is discarded instead of
    // writing A's data into B's screen (same job the effect's `cancelled` flag
    // does for the initial load, but for refreshes started outside the effect).
    const liveFetchSeq = useRef(0);
    useEffect(() => {
        liveFetchSeq.current += 1;
        if (!businessId || isFixtureBusiness) {
            setLiveDetail(undefined);
            setLiveConnectors([]);
            return;
        }
        let cancelled = false;
        // Both reset together. The `cancelled` guard already stops a slow
        // response from the PREVIOUS business landing in state, but connectors
        // also have to be cleared up front: without this, the window between
        // navigating to B and B's connectors arriving still renders A's.
        setLiveDetail(undefined);
        setLiveConnectors([]);
        void getBusinessDetail(businessId).then((result) => {
            if (!cancelled) setLiveDetail(result);
        });
        void getBusinessConnectors(businessId).then((c) => {
            if (!cancelled) setLiveConnectors(c);
        });
        // The version list. Cheap, and it is what tells the header whether this
        // business has more than one scan to offer at all.
        setScanHistory([]);
        void getScoreHistory(businessId).then((h) => {
            if (!cancelled) setScanHistory(h);
        });
        return () => {
            cancelled = true;
        };
    }, [businessId, isFixtureBusiness]);

    /**
     * Re-fetch BOTH live payloads after something changed them server-side — a
     * connector connect/disconnect, or an on-demand connector sync.
     *
     * Both, because they answer different questions: the connector catalog
     * says what is linked, while the search numbers (Clicks / Impressions /
     * CTR / Position) ride on the business detail via
     * `latest_assessment.results.searchPerformance`. Refreshing only the
     * catalog after a Search Console sync left the table of dashes that
     * prompted the sync in the first place.
     *
     * Deliberately does NOT reset state to the loading placeholder: the
     * numbers on screen stay up until their replacements arrive, so a refresh
     * reads as an update, not a reload.
     */
    const refreshLiveData = (): Promise<void> => {
        if (!businessId || isFixtureBusiness) return Promise.resolve();
        // ADVANCE the token, don't just read it. Reading it meant two refreshes
        // for the same business captured the same value, so both passed the
        // guard below and whichever RESOLVED last won — even when it was the
        // older request. This PR made that reachable by adding three overlapping
        // triggers (the Recommendations Refresh, the Tracker write, and the
        // issue-status write). Incrementing per refresh composes with the
        // per-navigation increment in the effect above: both simply move a
        // monotonic counter, and only the newest holder of it may write state.
        const seq = ++liveFetchSeq.current;
        // Returns a promise so a caller can show progress that actually tracks
        // the work (#3512) — a fire-and-forget refresh made the Recommendations
        // spinner flash for a frame, which reads as "nothing happened". The
        // sequence guard still discards a refresh for business A that lands
        // after the user has switched to business B.
        return Promise.all([
            getBusinessDetail(businessId).then((result) => {
                if (liveFetchSeq.current === seq) setLiveDetail(result);
            }),
            getBusinessConnectors(businessId).then((c) => {
                if (liveFetchSeq.current === seq) setLiveConnectors(c);
            }),
        ]).then(() => undefined);
    };

    // The workspace stays mounted across its screens, so the detail read on arrival (while the First
    // AI check was still running) would stay on screen: "Review the answers" opened an empty Visibility
    // until a reload (Jovi, 21 Oct). Opening the answers reads the business again, and its scan list.
    const refreshRef = useRef(refreshLiveData);
    refreshRef.current = refreshLiveData;
    const lastScreen = useRef(screen);
    useEffect(() => {
        const from = lastScreen.current;
        lastScreen.current = screen;
        if (screen !== "questions" || from === "questions" || !businessId || isFixtureBusiness) return;
        void refreshRef.current();
        let cancelled = false;
        void getScoreHistory(businessId).then((h) => {
            if (!cancelled) setScanHistory(h);
        });
        return () => {
            cancelled = true;
        };
    }, [screen, businessId, isFixtureBusiness]);

    const rawDetail = liveDetail?.ok ? liveDetail.detail : null;
    const latestScanId = rawDetail?.latest_assessment?.assessment_id ?? null;
    /** Asking for the newest scan by id is the same as asking for nothing. */
    const wantsPastScan = !!selectedScanId && selectedScanId !== latestScanId;

    // Load the chosen scan. Same shape as `latest_assessment`, so it can simply
    // take its place below and every tab follows without knowing.
    useEffect(() => {
        if (!businessId || isFixtureBusiness || !wantsPastScan || !selectedScanId) {
            setPastScan(null);
            return;
        }
        let cancelled = false;
        setPastScan("loading");
        getAssessment(selectedScanId)
            .then((a) => { if (!cancelled) setPastScan(a); })
            // A 404 or a 403: an id from another business, or one that is gone.
            // Neither is worth an error screen — the workspace falls back to the
            // latest scan and says so.
            .catch(() => { if (!cancelled) setPastScan("missing"); });
        return () => { cancelled = true; };
    }, [businessId, isFixtureBusiness, wantsPastScan, selectedScanId]);

    /**
     * The scan every tab reads (#3933).
     *
     * ONE SUBSTITUTION, and the whole workspace follows. `latest_assessment` is
     * what every screen is built from, so swapping it here is the difference
     * between a version selector and a rewrite of eleven screens.
     */
    const viewingPast = wantsPastScan && pastScan !== null && pastScan !== "missing";
    // Unwrapped once. Declared up here rather than beside the other derived
    // values below because the tracker effect needs it, and an effect cannot be
    // declared after an early return.
    const detail = rawDetail && viewingPast && typeof pastScan === "object"
        // The cast is the backend's own guarantee, not a shortcut: GET
        // /assessments/{id} returns the same `to_status_dict()` plus
        // `searchPerformance` that the detail route embeds. The two TS types
        // were written separately and differ only in which fields each one
        // bothered to declare optional.
        ? { ...rawDetail, latest_assessment: pastScan as unknown as NonNullable<typeof rawDetail>["latest_assessment"] }
        : rawDetail;
    /** Back to the newest scan, without leaving the tab. */
    const backToLatest = useCallback(() => {
        const next = new URLSearchParams(searchParams);
        next.delete("scan");
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams]);
    const openScan = useCallback((id: string) => {
        const next = new URLSearchParams(searchParams);
        if (!id || id === latestScanId) next.delete("scan");
        else next.set("scan", id);
        setSearchParams(next, { replace: false });
    }, [searchParams, setSearchParams, latestScanId]);

    // The execution tracker (ourteam-backend#3686). Generated server-side on
    // first fetch, so asking for it is what CREATES this business's board —
    // which is why it is fetched only when the Tracker tab is actually open,
    // and only for a business whose latest run actually produced results.
    //
    // That second condition has to live in the effect, not in the render-time
    // guard further down: an early return does not stop an effect that has
    // already been declared, so gating only on render would still let someone
    // manufacture a persisted tracker for a gated or unscored assessment just
    // by opening the tab — a plan that could then outlive the failed run and
    // reappear against a later healthy one.
    const trackerAssessmentId = detail?.latest_assessment?.assessment_id;
    /**
     * The ids the write controls key on — withheld while a past scan is open
     * (#3933).
     *
     * Reusing the absent-id path rather than adding a `readOnly` prop to eleven
     * screens: every one of those controls was already built to render nothing
     * without an id, because a fixture business has none. A past scan is the
     * same claim — there is nothing here you may write to — so it takes the
     * same route, and no screen needs to learn a second way to be read-only.
     */
    const writableAssessmentId = viewingPast ? undefined : trackerAssessmentId;
    const writableBusinessId = viewingPast ? undefined : businessId ?? undefined;
    const canFetchTracker =
        (screen === "todo" || screen === "reviews") && !!trackerAssessmentId && !!detail && hasLiveResults(detail);
    const [liveTracker, setLiveTracker] = useState<BusinessTracker | undefined>(undefined);
    /** The plan read failed: Fixes says so and still lists the recommendations,
     *  rather than spinning on a read that is not coming. */
    const [trackerFailed, setTrackerFailed] = useState(false);
    useEffect(() => {
        setTrackerFailed(false);
        if (!canFetchTracker || !trackerAssessmentId) {
            setLiveTracker(undefined);
            return;
        }
        let cancelled = false;
        void getTracker(trackerAssessmentId).then((raw) => {
            if (cancelled) return;
            if (raw) setLiveTracker(adaptTracker(raw));
            else setTrackerFailed(true);
        });
        return () => { cancelled = true; };
    }, [canFetchTracker, trackerAssessmentId]);

    /**
     * Merchant Home's "How you're found": the count merchant Visibility shows,
     * built from the same inputs that screen reads (see "questions" below),
     * off the business detail fetched above rather than a second request.
     * Home is the only merchant view that reads it, so it is built there only,
     * and once per detail rather than on every render. It never holds the
     * page: undefined while the detail loads, so the tile draws a placeholder;
     * null when it could not be read.
     */
    const merchantLive = useMemo(() => {
        if (role === "oura-staff" || screen !== "dashboard" || !businessId) return undefined;
        if (isFixtureBusiness) return merchantReading(visibilityFixture(businessId));
        if (liveDetail === undefined) return undefined;
        if (!liveDetail.ok) return null;
        const live = liveDetail.detail;
        return merchantReading(
            hasLiveResults(live) && live.latest_assessment
                ? visibilityInputsFromAssessment(live.latest_assessment, {
                    capturedAt: buildLiveSnapshot(live).capturedAt,
                    history: scanHistory,
                })
                : null,
        );
    }, [role, screen, businessId, isFixtureBusiness, liveDetail, scanHistory]);

    // Clients never browse business drill-ins. Their data is already pinned to
    // their own workspace above (the URL's :businessId is discarded), but the
    // explicit redirect enforces the boundary instead of implying it — and a
    // client never sits on another business's URL.
    if (role !== "oura-staff" && params.businessId) {
        return <Navigate to={`${OURA_ROOT}/dashboard`} replace />;
    }
    // Client, business still resolving from the login.
    if (role !== "oura-staff" && myBusinessId === undefined) {
        return (
            <OuraShell>
                <div className="flex justify-center py-24">
                    <Loader2Icon className="h-6 w-6 animate-spin text-gray-400" />
                </div>
            </OuraShell>
        );
    }
    // The access a client gives us. Their own record, read through their own
    // token, so it never goes near the staff business detail below.
    if (screen === "access") {
        if (role === "oura-staff" || !showOnboarding) {
            return <Navigate to={role === "oura-staff" ? `${OURA_ROOT}/businesses` : `${OURA_ROOT}/dashboard`} replace />;
        }
        return (
            <OuraShell>
                <Access />
            </OuraShell>
        );
    }

    // The merchant's old Team page: who can sign in now lives on Settings >
    // Manage Team Access, so an old link or bookmark lands there. Staff manage
    // logins from the business.
    if (screen === "team") {
        if (role === "oura-staff") return <Navigate to={`${OURA_ROOT}/businesses`} replace />;
        return <Navigate to={OURA_TEAM_ACCESS_PATH} replace />;
    }

    // Client views that live only in the approval layer. `schedule` is Work's
    // retired name: /oura/schedule redirects, so no route sends it today, but a
    // View value that still exists opens Work rather than falling through to
    // the staff branches below.
    if (role !== "oura-staff" && (screen === "decisions" || screen === "reports" || screen === "work" || screen === "schedule" || screen === "dashboard")) {
        if (clientPage === undefined) {
            return (
                <OuraShell>
                    <div className="flex justify-center py-24">
                        <Loader2Icon className="h-6 w-6 animate-spin text-gray-400" />
                    </div>
                </OuraShell>
            );
        }
        if (!clientPage.ok) {
            return (
                <OuraShell>
                    <ClientPageError reason={clientPage.reason} previewing={previewingAsClient} onRetry={() => void refreshClientPage()} />
                </OuraShell>
            );
        }
        return (
            <OuraShell>
                {/* Until the set-up is finished it is the first thing on the
                    merchant's home. It renders nothing once it is. The
                    redesign's "Here is how this works" replaces it, inside
                    Home under the title (#4062 MR-FE-10). */}
                {screen === "dashboard" && showOnboarding && !MERCHANT_V2 && <SetupBanner />}
                {/* Mounted once for the whole client surface, so "How this
                    works" starts it from any page (#4062 MR-FE-10). It
                    renders nothing until something asks it to. */}
                {MERCHANT_V2 && <MerchantTour />}
                <ClientPage
                    payload={clientPage.payload}
                    live={merchantLive}
                    view={screen === "dashboard" ? "progress" : screen === "schedule" ? "work" : screen}
                    focusItemId={focusItemId}
                    onRefresh={refreshClientPageFresh}
                />
            </OuraShell>
        );
    }

    // Staff roll-up home — no single business in focus. Clients don't have it.
    if (screen === "overview") {
        if (role !== "oura-staff") return <Navigate to={`${OURA_ROOT}/dashboard`} replace />;
        return (
            <OuraShell>
                <Overview
                    onOpenBusiness={(id) =>
                        navigate(ouraHref("dashboard", { role, businessId: id }))
                    }
                />
            </OuraShell>
        );
    }

    // Staff pipeline — the only other screen with no single business in focus.
    if (screen === "businesses") {
        return (
            <OuraShell>
                <Businesses
                    onSelectBusiness={(id, view = "dashboard") => {
                        const to = ouraHref(view, { role, businessId: id });
                        // A business is a workspace, not a page: staff work one
                        // for an hour and keep two or three clients open at
                        // once. Opening it in its own tab is what lets the
                        // platform sidebar step aside inside it (OuraShell)
                        // while this list stays exactly where it was.
                        // `noopener` because the child tab has no business
                        // reaching back into this one.
                        if (isBusinessSidebarLive) {
                            window.open(to, "_blank", "noopener");
                            return;
                        }
                        navigate(to);
                    }}
                />
            </OuraShell>
        );
    }

    // The Skill library (Docs phase 2): staff only, no business in focus. The
    // route serves it only where DOCS_WRITE_ON; the screen says so itself too.
    if (screen === "skills") {
        if (role !== "oura-staff") return <Navigate to={`${OURA_ROOT}/dashboard`} replace />;
        return (
            <OuraShell>
                <Skills />
            </OuraShell>
        );
    }

    // The Tracker (DeliveryBoard): staff only, no business in focus. The route serves
    // it only where DELIVERY_ON.
    if (screen === "board") {
        if (role !== "oura-staff") return <Navigate to={`${OURA_ROOT}/dashboard`} replace />;
        return (
            <OuraShell>
                <DeliveryBoard />
            </OuraShell>
        );
    }

    // Client requests: staff only, no business in focus. The route serves it
    // only where REQUESTS_ON.
    if (screen === "requests") {
        if (role !== "oura-staff") return <Navigate to={`${OURA_ROOT}/dashboard`} replace />;
        return (
            <OuraShell>
                <ClientRequests />
            </OuraShell>
        );
    }

    // Form answers (#4065): staff only, no business in focus. A client who
    // types the URL lands on their own home, like every other staff screen.
    if (screen === "formAnswers") {
        if (role !== "oura-staff") return <Navigate to={`${OURA_ROOT}/dashboard`} replace />;
        return (
            <OuraShell>
                <FormAnswers />
            </OuraShell>
        );
    }

    // AEO Assessment (#3065) — staff-facing, top-level (no single business in
    // focus; the flow itself is keyed off the domain the staffer types/picks).
    if (screen === "assessment") {
        if (role !== "oura-staff") return <Navigate to={`${OURA_ROOT}/dashboard`} replace />;
        return (
            <OuraShell>
                <AeoAssessmentScreen />
            </OuraShell>
        );
    }

    // A real id from GET /api/v1/businesses (#3152) isn't one of the three
    // bundled fixtures — resolve it from the live fetch above instead (#3160).
    if (businessId && !isFixtureBusiness) {
        if (liveDetail === undefined) {
            return (
                <OuraShell>
                    <div className="flex justify-center py-24">
                        <Loader2Icon className="h-6 w-6 animate-spin text-gray-400" />
                    </div>
                </OuraShell>
            );
        }
        // A genuine 404: the id does not resolve, so the list is where to go.
        if (!liveDetail.ok && liveDetail.kind === "notFound") {
            return <Navigate to={`${OURA_ROOT}/businesses`} replace />;
        }
        // Anything else is a failure to ASK, not an answer. Say so and offer a
        // retry — silently bouncing to the list on a transient 500 is how a
        // working business looks permanently deleted.
        if (!liveDetail.ok) {
            return (
                <OuraShell>
                    <Card>
                        <CardBody>
                            <EmptyState
                                icon={AlertTriangleIcon}
                                title="Couldn't load this business"
                                description="Something went wrong reaching the server — the business is still there. Try again, and if it keeps happening your session may have expired."
                                action={
                                    <Button onClick={() => window.location.reload()}>Try again</Button>
                                }
                            />
                        </CardBody>
                    </Card>
                </OuraShell>
            );
        }
    }

    const snapshot = isFixtureBusiness
        ? getSnapshot(businessId!)
        : detail
            ? buildLiveSnapshot(detail)
            : null;
    const business = isFixtureBusiness
        ? getBusiness(businessId!)
        : detail
            ? buildLiveBusiness(detail)
            : null;
    if (!snapshot || !business) {
        // No businessId at all (bad URL) → staff back to the list.
        return <Navigate to={`${OURA_ROOT}/businesses`} replace />;
    }

    const onNavigate = (v: View) => navigate(ouraHref(v, { role, businessId }));
    /**
     * Open the Tracker anchored on a task whose id is already known (#4445).
     *
     * The Site Audit drawer has joined the task itself off the summary route,
     * so there is nothing left to resolve: it goes straight to the canonical
     * `?task=<id>` form.
     */
    const onOpenTrackerTaskById = (taskId: string) =>
        navigate(`${ouraHref("todo", { role, businessId })}?task=${encodeURIComponent(taskId)}`);
    /** Open one finding on Site issues, where a fix raised by a check is drafted. */
    const onOpenSiteIssue = (issueKey: string) =>
        navigate(`${ouraHref("setup", { role, businessId })}?issue=${encodeURIComponent(issueKey)}`);
    /**
     * Open Client, on one card when given it: a fix's "Open the documents"
     * lands on the Documents card, where the client is asked now (YW, 29 Sep
     * 2026; it used to open "Choose what to ask" with the fix ticked), and
     * Fixes' "N questions on Client" on Questions to the client.
     */
    const onOpenClient = (card?: "documents" | "questions") =>
        navigate(ouraHref("reviews", { role, businessId }) + (card ? `#${card}` : ""));
    /**
     * Open the Pages workshop for one page.
     *
     * The page rides in the URL rather than in parent state, so a deep link
     * survives a refresh, a shared link and the back button — and, unlike a
     * remembered selection, opening the Pages tab on its own always lands on
     * the list instead of reopening whatever was last clicked.
     */
    const onOpenPage = (page: { url: string; title: string }) =>
        navigate(
            `${ouraHref("pages", { role, businessId })}?page=${encodeURIComponent(page.url)}` +
                `&title=${encodeURIComponent(page.title)}`,
        );
    const onOpenReport = () => navigate(ouraHref("report", { role, businessId }));

    // The report is a frozen, full-bleed deliverable — no shell, just a
    // prototype-only "back" affordance clearly outside the report.
    // Declared above the report early-return, which is the only consumer that
    // runs before the block of live adapters further down.
    const liveMetrics = !isFixtureBusiness && detail ? adaptReportMetrics(detail) : undefined;
    // A live scan's report prints only what the scan measured (#4238).
    const liveReport = !isFixtureBusiness && detail ? liveHeadline(detail) : undefined;

    // A business opened by its website (/businesses/riversidedental.co/onboarding) is the same
    // business as its id route: the server resolves either, and every tab keys on the id, so
    // the URL moves to the id and the same screen opens (Angel, 15 Oct).
    const canonicalId = rawDetail?.id;
    if (businessId && canonicalId && canonicalId !== businessId) {
        const { pathname, search, hash } = window.location;
        const from = `/businesses/${encodeURIComponent(businessId)}`;
        const at = pathname.indexOf(from);
        if (at >= 0) {
            const to = `${pathname.slice(0, at)}/businesses/${encodeURIComponent(canonicalId)}${pathname.slice(at + from.length)}`;
            return <Navigate to={`${to}${search}${hash}`} replace />;
        }
    }

    if (screen === "report") {
        return (
            <div className="min-h-screen bg-gray-100">
                <button
                    onClick={() => onNavigate("dashboard")}
                    className="fixed left-4 top-4 z-30 inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-sm font-medium text-gray-600 shadow-md hover:text-gray-900"
                >
                    <ArrowLeftIcon className="h-4 w-4" />
                    Back to Oura
                </button>
                <Report snapshot={snapshot} business={business} metrics={liveMetrics} live={liveReport} />
            </div>
        );
    }

    // Staff reach a business from the list; its views are page tabs inside the
    // BusinessDetail chrome — same rail throughout (gameplan A3). Clients keep
    // their flat per-route screens unchanged.
    const isStaffDrillIn = role === "oura-staff" && !!params.businessId;


    // Connectors, Pages and Activity are staff-only chrome; hidden in nav is
    // not protected, so a client URL hitting any of them falls back home.
    if ((screen === "connectors" || screen === "pages" || screen === "activity" || screen === "reviews" || screen === "documents" || screen === "knowledge" || screen === "contract" || screen === "timeline" || screen === "delivery" || screen === "onboarding") && !isStaffDrillIn) {
        return <Navigate to={`${OURA_ROOT}/dashboard`} replace />;
    }

    // Onboarding is hidden until its endpoints ship. A link to it lands on the
    // business instead of on an empty screen.
    if (screen === "onboarding" && !showOnboarding) {
        return <Navigate to={ouraHref("dashboard", { role, businessId })} replace />;
    }

    // Real-data overrides (ourteam-frontend#3160) — only meaningful for a live
    // (non-fixture) business. adaptBusinessAudit returns null
    // when the latest run has no usable results yet (gated, or not scored) —
    // coalesced to undefined so the screens fall back to their own default
    // ONLY for a fixture business, never silently for a live one (that fallback
    // path is gated out entirely below).
    const liveAudit = !isFixtureBusiness && detail ? adaptBusinessAudit(detail) ?? undefined : undefined;
    // Which instrument produced the visibility number (#3564). Fixtures have
    // none, and a scan from before it was recorded returns null — both mean the
    // surfaces say nothing about it rather than guessing.
    const liveGrounding = !isFixtureBusiness && detail ? adaptGrounding(detail) : null;
    // Where this business's question set was re-authored (#3539). Live only —
    // a fixture trend is generated and has never been re-authored.
    const liveTrendBreak = !isFixtureBusiness && detail ? adaptTrendBreak(detail) : null;
    const liveSearch = !isFixtureBusiness && detail ? adaptSearchPerformance(detail) : undefined;

    const liveConnectorList = !isFixtureBusiness ? adaptConnectors(liveConnectors) : null;

    /**
     * Why the Tracker drawer's publish buttons cannot work right now (#3400):
     * no CMS-category connector is connected, so there is nothing to publish
     * through. Undefined while the catalog is loading (an empty list is the
     * loading state — same reasoning as gscState below) and on fixtures, where
     * publish buttons don't render at all.
     */
    const cmsPublishBlockedReason =
        liveConnectorList && liveConnectorList.length > 0 &&
        !liveConnectorList.some((c) => c.category === "cms" && c.status === "connected")
            ? "No connected website platform for this business. Connect one on the Connectors tab to publish from here."
            : undefined;

    /**
     * WHICH platform a publish would write to — the connected CMS connector's
     * own name (#3547). The Workshop's confirm dialog used to say "WordPress"
     * unconditionally; on a Framer or Wix business that was simply false, and
     * this is the dialog standing between an operator and a client's live site.
     */
    const publishTargetLabel =
        liveConnectorList?.find((c) => c.category === "cms" && c.status === "connected")?.label
        ?? "";

    /**
     * Why the connected platform cannot publish a given draft type
     * (ourteam-backend#4761 / #3692), or undefined when it can.
     *
     * The connected CMS declares what it writes: WordPress all four types, Wix
     * only `meta_description` and `schema_jsonld`, because a Wix page body is
     * not addressable HTML. Without this, a Wix client is shown a Publish
     * button whose only possible outcome is a 422.
     *
     * **Absent means unknown, and unknown means unchanged** — the house rule
     * `propertyMismatch` keeps. A backend that never learned to send
     * `publishableTypes` must behave exactly as it does today, so the resolver
     * itself is undefined in that case rather than returning "cannot publish"
     * for every type, which would hide every Publish button in the product.
     */
    const cmsConnector = liveConnectorList?.find((c) => c.category === "cms" && c.status === "connected");
    const publishUnsupported = cmsConnector?.publishableTypes
        ? (draftType: string) => {
            const types = cmsConnector.publishableTypes;
            if (!types || types.includes(draftType)) return undefined;
            // Names the platform, because "cannot be published" alone reads as
            // a bug in Oura rather than a limit of the client's own website.
            return `${cmsConnector.label} has no way to publish ${publishableTypeLabel(draftType)}. Follow the steps below instead.`;
        }
        : undefined;

    // What Search Console ACTUALLY is on this business, reconciled from the
    // connector catalog and the scan's own search block (see gscState.ts).
    // Derived once, here, and handed to every surface that shows or implies a
    // connection — the connectors card, the search screen and the pages
    // workshop each used to decide it for themselves and disagreed.
    //
    // Undefined for a fixture business: its connector catalog is generated
    // demo data with no scan behind it, so reconciling the two would report a
    // problem about a business that does not exist.
    //
    // Also undefined until the catalog actually arrives. An empty list is the
    // loading state as much as it is a real answer, and deriving from it would
    // flash "Not connected" at an operator whose connection is fine — a claim
    // about data we have not fetched yet. Screens fall back to their own
    // pre-reconciliation copy for that instant.
    const gscState = isFixtureBusiness || !liveConnectorList?.length
        ? undefined
        : deriveGscState({
            connector: findGscConnector(liveConnectorList),
            search: liveSearch,
            crawledPages: liveAudit?.pagesCrawled ?? null,
            siteDomain: business.domain,
            // The Site screen's test for its clicks column, so the two agree.
            pageClicks: liveAudit?.priorityPages.some((p) => p.visits > 0) ?? false,
        });

    /** The version control and its warning. Both are null on a fixture business
     *  and on a business with a single scan. Built before the "not yet scored"
     *  return below, because a past scan that produced no score lands there
     *  and still needs its way back. */
    const scanPicker = !isFixtureBusiness && scanHistory.length > 1 ? (
        <ScanVersionPicker
            history={scanHistory}
            selectedId={viewingPast ? selectedScanId : null}
            latestId={latestScanId}
            onOpen={openScan}
        />
    ) : null;
    /**
     * The business's stage facts (JOURNEY-SPEC v1.1 §2.2), from the LATEST
     * detail whichever scan is on screen. The header, the Overview Next card
     * and the empty states read the stage BusinessDetail works out from them.
     */
    // The condition BusinessDetail turns the stage on with: off (no draft service here, or not
    // staff), the screens keep today's empty state rather than a stage nobody reads.
    const hubLive = SCAN_DRAFT_UI_ON && scanDraftMode() !== "absent" && role === "oura-staff";
    // A sample business (data.ts): from its list row, so the header, the Next card and the row
    // action read one source; the contract only where the list has no row for it.
    const contractFacts: HubFacts = { businessId: business.id, name: business.name, domain: business.domain, state: business.state, scanned: true, scannedAt: snapshot.capturedAt, run: null, nextScanAt: null };
    const hubFacts: HubFacts | undefined = !hubLive
        ? undefined
        : isFixtureBusiness
            ? fixtureRow === undefined
                ? { ...contractFacts, unread: true }
                : fixtureRow
                    ? { ...factsFromRow(fixtureRow, null), name: business.name }
                    : contractFacts
            : rawDetail ? factsFromDetail(rawDetail) : undefined;
    const rescanInfo = rescanInfoFrom(scanHistory);
    /** The chosen past run has arrived, so `detail` now carries it. */
    const pastScanLoaded = viewingPast && typeof pastScan === "object";
    const scanBanner = pastScan === "missing"
        ? <ScanNotFoundBanner onBackToLatest={backToLatest} />
        : viewingPast
            // The same field the picker prints (`captured_at`), so the banner
            // and the control that opened it can never name two different days.
            // `completed_at` is a different instant on a run that finishes
            // after its capture, and this is the one line a reader checks the
            // whole screen against.
            ? MERCHANT_V2 && role !== "oura-staff"
                ? <PastScanBar
                    capturedAt={scanHistory.find((h) => h.assessment_id === selectedScanId)?.captured_at
                        ?? detail?.latest_assessment?.completed_at
                        ?? detail?.latest_assessment?.created_at}
                    onBackToLatest={backToLatest}
                  />
                : <PastScanBanner
                capturedAt={scanHistory.find((h) => h.assessment_id === selectedScanId)?.captured_at
                    ?? detail?.latest_assessment?.completed_at
                    ?? detail?.latest_assessment?.created_at}
                // Only once the past run has loaded: until then `detail` is
                // still the latest scan, and its results say nothing about this one.
                unscored={pastScanLoaded && !!detail && !hasLiveResults(detail)}
                onBackToLatest={backToLatest}
              />
            : null;

    /**
     * Delivery, Plan and Timeline read the engagement, not a scan, so they
     * render whether or not a scored scan exists. The onboarding Next card's
     * "Open the timeline" landed on "Not yet scored" for every client whose
     * first check had not produced results here (Angel, 15 Oct): the schedule
     * is published from the plan, and a gated baseline must not hide it.
     */
    const engagementScreen = isStaffDrillIn && !!businessId && (screen === "delivery" || screen === "contract" || screen === "timeline");
    const engagementChecks = () => contractChecks(
        (viewingPast ? (!isFixtureBusiness && rawDetail ? adaptBusinessAudit(rawDetail) ?? undefined : undefined) : liveAudit) ??
            (isFixtureBusiness ? getAudit(business, snapshot) : undefined),
    );
    const engagementView = (): ReactNode => {
        if (!engagementScreen || !businessId) return null;
        if (screen === "timeline") return <TimelineTab businessId={businessId} />;
        if (screen === "contract") return <ContractTab businessId={businessId} staffName={DEMO_STAFF.name} checks={engagementChecks()} />;
        return (
            <Delivery
                businessId={businessId}
                businessName={business.name}
                onGoTo={(v, search) => navigate(ouraHref(v as View, { role, businessId }) + (search ?? ""))}
                contract={<ContractTab businessId={businessId} staffName={DEMO_STAFF.name} checks={engagementChecks()} />}
                timeline={
                    <TimelineTab
                        businessId={businessId}
                        // A publish conflict is fixed on the Plan view of
                        // this same screen, in the field that clears it.
                        onFixConflict={(field) => navigate(`${ouraHref("delivery", { role, businessId })}?view=plan&focus=${encodeURIComponent(field)}`)}
                    />
                }
            />
        );
    };

    // A live business with no scored results yet (still queued/running, or
    // gated) has nothing real to show on ANY tab — one shared "not yet scored"
    // state instead of three tabs each rendering a different kind of empty.
    if (!isFixtureBusiness && detail && !hasLiveResults(detail)) {
        const la = detail.latest_assessment;
        const gated = !!la?.gated;
        /**
         * The merchant hits this gate too, and it is the COMMON way to be
         * unmeasured. A business with no completed run at all never reaches
         * the Visibility screen's own empty state, because this returns first.
         *
         * What it returned was BusinessDetail: the staff drill-in chrome, with
         * the breadcrumb back to a list the merchant cannot open, the scan
         * controls, the spend line, and nine tabs of ours. Nested inside the
         * client rail. The copy under it told them to "run a New Scan from the
         * Businesses table", which is an instruction to a colleague.
         *
         * So the chrome branches, not just the wording. The copy branches on
         * the two facts we hold here. No `latest_assessment` means no run has
         * ever been recorded against this business, and that is the ONLY case
         * where "not measured yet" is a claim we can stand behind: a run that
         * exists and produced nothing usable says so about THAT run, because an
         * earlier scored run would not show up in this payload and telling a
         * measured business it has never been measured is the same lie in the
         * other direction. A queued or running scan is its own state rather
         * than a missing result, and saying "no result" over a scan that is
         * still working reads as a failure.
         */
        if (role !== "oura-staff") {
            // A merchant still in setup gets the first-run card here too: a
            // business with no scan at all is exactly the "starts after step 1"
            // case it was written for, and returning first hid it (#4430).
            if (screen === "questions" && role === "oura-client" && firstRun) {
                return <OuraShell><FirstCheckBlank run={firstRun} /></OuraShell>;
            }
            const running = la?.status === "queued" || la?.status === "running";
            return (
                <OuraShell>
                    <Card>
                        <CardBody>
                            <EmptyState
                                icon={SparklesIcon}
                                title={!la ? "Not measured yet" : running ? "Scan in progress" : "No result from the last scan"}
                                description={!la
                                    ? waitsForBrand ? BRAND_WAIT_LINE : `We have not scanned ${business.name} yet.`
                                    : running
                                        ? `We are scanning ${business.name} now.`
                                        : `The last scan of ${business.name} produced no result to show.`}
                            />
                        </CardBody>
                    </Card>
                </OuraShell>
            );
        }
        // A past scan opened from the picker that ended without a report
        // (failed, cancelled, gated): the business has scans, so "not yet
        // scored" would be false. Say why this run has nothing, when the
        // backend sent a reason, and keep the picker and the way back.
        const showReason = gated || (pastScanLoaded && !!la?.error_code);
        return (
            <OuraShell>
                <BusinessDetail
                    business={business}
                    snapshot={snapshot}
                    active={screen as DetailTab}
                    onNavigate={onNavigate}
                    onOpenReport={onOpenReport}
                    onRenamed={isFixtureBusiness ? undefined : refreshLiveData}
                    readOnly={viewingPast}
                    scanPicker={scanPicker}
                    hubFacts={hubFacts}
                    rescanInfo={rescanInfo}
                >
                    {scanBanner}
                    {/* The one screen a never-scanned business is FOR: a
                        client is created at signing, and its first scan only
                        starts once their form is answered. */}
                    {screen === "onboarding" && businessId && showOnboarding ? (
                        <OnboardingTab business={business} businessId={businessId} />
                    ) : engagementScreen ? (
                        engagementView()
                    ) : (
                    <>
                    {/* New clients are the unscanned ones, so their Client tab still says where set-up stands. */}
                    {screen === "reviews" && businessId && showOnboarding && (
                        <div className="mb-4">
                            <OnboardingStrip
                                businessId={businessId}
                                business={{ name: business.name, domain: business.domain }}
                                onboardingHref={ouraHref("onboarding", { role, businessId })}
                            />
                        </div>
                    )}
                    {/* New scan switch on: the stage says what comes next (§2.2).
                        Overview carries the Next card, so its empty state
                        leaves the press to it. A past scan, or a run with a
                        reason to show, keeps the panel below. */}
                    {hubFacts && !pastScanLoaded && !showReason ? (
                        <>
                            {screen === "dashboard" && <HubNextSlot className="mb-5" />}
                            {/* The Client tab's strip carries its own press there: one next step per screen. */}
                            <HubEmptyState name={business.name} withPress={screen !== "dashboard" && !(screen === "reviews" && showOnboarding)} />
                        </>
                    ) : (
                    <Card>
                        <CardBody>
                            <EmptyState
                                icon={SparklesIcon}
                                title={pastScanLoaded ? "No score from this scan" : gated ? "No usable score from the last scan" : "Not yet scored"}
                                description={
                                    showReason
                                        // The reason lives in the panel below rather than
                                        // here — a generic sentence followed by the actual
                                        // cause reads as an apology followed by the answer.
                                        ? undefined
                                        : pastScanLoaded
                                            ? "This run ended without a report, so there is nothing to show from it."
                                            : "This business has no completed scan yet. Run a New Scan from the Businesses table to get its first score."
                                }
                                action={
                                    showReason ? (
                                        <GatedReason
                                            errorCode={la?.error_code}
                                            errorDetail={la?.error_detail}
                                            notice={la?.notice_detail}
                                            stage={la?.stage}
                                            status={la?.status}
                                        />
                                    ) : undefined
                                }
                            />
                        </CardBody>
                    </Card>
                    )}
                    </>
                    )}
                </BusinessDetail>
            </OuraShell>
        );
    }

    const inner = (
        <>
            {/* Staff Overview tab = the executive audit view; the client keeps
                the calmer owner-facing Dashboard. Same split for Site Audit vs
                the client's "Your setup". */}
            {screen === "dashboard" && (isStaffDrillIn ? (
                <BusinessOverview
                    snapshot={snapshot}
                    business={business}
                    liveAudit={liveAudit}
                    grounding={liveGrounding}
                    // A fixture reads the demo block Search performance shows,
                    // so the Google tile and that screen state one position.
                    searchPerformance={isFixtureBusiness
                        ? devMockSearchPerformance(businessId ?? "")
                        : detail ? adaptSearchPerformance(detail) ?? null : null}
                    aiVisibilityRaw={!isFixtureBusiness && detail ? detail.latest_assessment?.results?.aiVisibility ?? null : null}
                    trendBreak={liveTrendBreak}
                    // The scan on screen, the block Visibility builds from, so
                    // the AI tile and the Visibility screen state one count.
                    assessment={isFixtureBusiness ? null : detail?.latest_assessment}
                    rating={liveMetrics
                        ? { level: liveMetrics.maturityLevel ?? null, cappedBy: liveMetrics.levelCappedBy ?? null }
                        : undefined}
                    gscState={gscState}
                    // Same rule as gscState: unknown until the catalog arrives, so
                    // the Overview never lists a connection problem it has not fetched.
                    connectors={liveConnectorList?.length ? liveConnectorList : undefined}
                    ga4Connector={isFixtureBusiness || !liveConnectorList?.length
                        ? undefined
                        : liveConnectorList.find((c) => c.key === "ga4") ?? null}
                    scanHistory={scanHistory}
                    latestAttempt={isFixtureBusiness ? null : detail?.latest_attempt ?? null}
                    nextScanAt={isFixtureBusiness ? null : detail?.next_auto_scan_at ?? null}
                    // The plan hangs off the latest scan. Withheld on a past
                    // scan, where today's plan is not shown beside old figures.
                    assessmentId={isFixtureBusiness ? undefined : writableAssessmentId}
                    viewingPast={viewingPast}
                    onBackToLatest={backToLatest}
                />
            ) : (
                <Dashboard
                    snapshot={snapshot}
                    business={business}
                    onNavigate={onNavigate}
                    onOpenReport={onOpenReport}
                />
            ))}
            {screen === "questions" && role === "oura-staff" && showOnboarding && (
                // The first AI check's results wait here to be read and published (Jack, 20 Oct 2026).
                <FirstResultsBanner key={business.id} businessId={business.id} onboardingHref={ouraHref("onboarding", { role, businessId: business.id })} />
            )}
            {/* Not known yet whether first run replaces the page: draw neither, never the charts it hides (#4440). */}
            {screen === "questions" && role === "oura-client" && firstRunPending ? null : screen === "questions" && role === "oura-client" && firstRun ? (
                <FirstCheckBlank run={firstRun} />
            ) : screen === "questions" && (
                // One screen for both roles. A live business is read off the
                // scan on screen (a past one included); a sample business
                // reads its own live-shaped payload, or shows the empty state
                // when it has none, never numbers generated from its id. A
                // real business whose run carries no aiVisibility block gets
                // the empty state for staff and merchant alike.
                <Visibility
                    businessId={business.id}
                    businessName={business.name}
                    capturedAt={snapshot.capturedAt}
                    assessment={isFixtureBusiness ? null : detail?.latest_assessment}
                    history={scanHistory}
                    fixture={isFixtureBusiness ? visibilityFixture(business.id) : null}
                    // Answers and captures are read from the scan on screen,
                    // past or latest; every write keys off the writable ids,
                    // which a past scan withholds.
                    readAssessmentId={isFixtureBusiness ? undefined : trackerAssessmentId}
                    writableAssessmentId={isFixtureBusiness ? undefined : writableAssessmentId}
                    // Passed on a fixture too: the demo queue is in devMock, so
                    // the Add / Queued / Undo path is exercisable without a
                    // Search Console property.
                    writableBusinessId={writableBusinessId}
                    // The merchant's next AI check: monthly, with the next
                    // report on their published schedule (client page).
                    nextCheckAt={clientPage?.ok ? nextAiCheckDate(clientPage.payload) : null}
                    // Staff: the next scheduled scan (#3919). A sample has none.
                    nextScanAt={isFixtureBusiness ? null : detail?.next_auto_scan_at ?? null}
                    searchHref={ouraHref("search", { role, businessId })}
                    gscState={gscState}
                    onRefreshed={isFixtureBusiness ? undefined : refreshLiveData}
                    onNavigate={onNavigate}
                    onOpenTask={onOpenTrackerTaskById}
                    // Staff open past scans from the business chrome; the
                    // client's picker sits in the page's own header.
                    scanControl={isStaffDrillIn ? undefined : scanPicker}
                    // A version picked on Named over time opens its latest scan,
                    // so every tab shows that version's questions.
                    onOpenScan={isFixtureBusiness ? undefined : openScan}
                    onBackToLatest={backToLatest}
                />
            )}
            {screen === "search" && (
                <SearchPerformance
                    // A fixture business has no Search Console: it gets the
                    // demo block, so the preview shows the screen, not its
                    // not-connected state.
                    search={isFixtureBusiness ? devMockSearchPerformance(businessId ?? "") : liveSearch}
                    businessName={business.name}
                    gscState={gscState}
                    // The same block the Visibility page reads (#3879), so the
                    // two screens can never disagree about one search.
                    searchQuestions={isFixtureBusiness
                        ? devMockSearchQuestions(businessId ?? "")
                        : detail?.latest_assessment?.results?.searchQuestions ?? null}
                    // The 50 as this workspace already has them, so a covered
                    // search opens the very action the Visibility table opens.
                    questions={snapshot.questions}
                    // GA4 sessions: the Traffic card moved here from the
                    // Overview (D4), with the same sources it read there.
                    traffic={isFixtureBusiness ? getAudit(business, snapshot).traffic : liveAudit?.traffic}
                    trafficEmpty={isFixtureBusiness ? null : liveAudit?.trafficEmpty ?? null}
                    // Same rule as gscState: unknown until the catalog arrives, so
                    // the card never flashes a state it has not fetched.
                    ga4Connector={isFixtureBusiness || !liveConnectorList?.length
                        ? undefined
                        : liveConnectorList.find((c) => c.key === "ga4") ?? null}
                    capturedAt={snapshot.capturedAt}
                    assessmentId={isFixtureBusiness ? undefined : writableAssessmentId}
                    businessId={writableBusinessId}
                    isStaff={isStaffDrillIn}
                    pastScan={viewingPast}
                    onRefreshed={isFixtureBusiness ? undefined : refreshLiveData}
                    onNavigate={onNavigate}
                />
            )}
            {screen === "setup" && (isStaffDrillIn ? (
                <SiteAudit
                    snapshot={snapshot}
                    business={business}
                    liveAudit={liveAudit}
                    businessId={isFixtureBusiness ? undefined : writableBusinessId}
                    // After an on-demand PageSpeed re-scan: the refreshed block
                    // rides on the business detail payload (#3401).
                    onRefreshData={!isFixtureBusiness && businessId ? refreshLiveData : undefined}
                    assessmentId={isFixtureBusiness ? undefined : writableAssessmentId}
                    onOpenTask={onOpenTrackerTaskById}
                    onNavigate={onNavigate}
                    // Every business: the Page workshop reads real pages now
                    // (see the PagesWorkshop mount below).
                    onOpenPage={onOpenPage}
                    // The block Search performance shows, fixtures included,
                    // so a page's clicks read the same on both screens.
                    liveSearch={isFixtureBusiness ? devMockSearchPerformance(businessId ?? "") : liveSearch}
                    pastScan={viewingPast}
                />
            ) : (
                <ClientSite
                    snapshot={snapshot}
                    business={business}
                    liveAudit={liveAudit}
                    businessId={isFixtureBusiness ? undefined : businessId ?? undefined}
                    scanControl={scanPicker}
                />
            ))}
            {screen === "pages" && (
                <PagesWorkshop
                    snapshot={snapshot}
                    business={business}
                    liveAudit={liveAudit}
                    // Same block and state as Search performance, so a sample
                    // that shows clicks there is not asked to connect here.
                    liveSearch={isFixtureBusiness ? devMockSearchPerformance(businessId ?? "") : liveSearch}
                    gscState={gscState}
                    businessId={isFixtureBusiness ? undefined : writableBusinessId}
                    /* The workshop is now open on every business.
                       ourteam-backend#4098 (page content), #4099 (link graph)
                       and #4101 (proposals, match, role, history) are live, so a
                       real business reads its own page and its own proposals —
                       the reason this was restricted to sample businesses is
                       gone.
                       WRITES, now. #4100 §1 shipped with PUBLISH_BLOCK_COPY
                       defaulted ON, so block-scoped copy and internal links can
                       be published to a connected WordPress site. The write is
                       gated by the same connector reason the Tracker drawer uses
                       (cmsPublishBlockedReason) and confirmed in the view before
                       it fires. */
                    workshopEnabled={true}
                    publishBlockedReason={cmsPublishBlockedReason}
                    publishTargetLabel={publishTargetLabel}
                    publishUnsupported={publishUnsupported}
                    onNavigate={onNavigate}
                    // After an on-demand Search Console sync from the nudge:
                    // the metric columns live on the business detail, so both
                    // payloads re-fetch (see refreshLiveData).
                    onRefreshData={!isFixtureBusiness && businessId ? refreshLiveData : undefined}
                />
            )}
            {/* The business's delivery record (#4508, #4531). Staff-only, like
                every other drill-in surface: it is what WE did, and it is the
                material a client update is written FROM, not the update. */}
            {screen === "activity" && isStaffDrillIn && (
                <ActivityLog assessmentId={trackerAssessmentId} />
            )}
            {/* The client approval layer (staff-only). Reads the same tracker the
                Tracker tab shows, so the two never disagree about which tasks exist. */}
            {screen === "reviews" && isStaffDrillIn && businessId && (
                isFixtureBusiness || liveTracker ? (
                    // Keyed by business: moving to another client starts the
                    // screen afresh, so nothing of the last client's questions,
                    // calls or open drawers ever shows under this one.
                    <ClientReviews
                        key={businessId}
                        business={business}
                        businessId={businessId}
                        tracker={liveTracker ?? getFixtureTracker(business, snapshot)}
                    />
                ) : (
                    <div className="flex justify-center py-24">
                        <Loader2Icon className="h-6 w-6 animate-spin text-gray-400" />
                    </div>
                )
            )}

            {/* Delivery (proposal v1.6, 30 Sep 2026), Plan and the dated schedule (#3714):
                staff-only by the guard above, and they read the engagement, not a scan. */}
            {engagementView()}
            {/* Staff-only (the guard above bounces a client), and rendered for
                any business with an id — including a fixture one. The service
                layer, not this component, decides where the store lives: with
                VITE_ENABLE_OURA_LIVE_API unset every knowledge call goes to
                devMock, which is the only way this tab is reviewable before
                ourteam-backend#4749 ships. */}
            {/* Documents: Client documents and Knowledge, each its own address under one
                rail item, switched by underlined page tabs (Jovi, 21 Oct). */}
            {(screen === "documents" || screen === "knowledge") && businessId && (
                <DocumentsTabs businessId={businessId} value={screen} />
            )}
            {/* Keyed by business: another client's reports must never paint under this one,
                not even for the frame before the read starts over (Jack, #4496). */}
            {screen === "documents" && businessId && <ClientDocuments key={businessId} businessId={businessId} businessName={business.name} />}
            {screen === "knowledge" && businessId && (
                <Knowledge businessId={businessId} />
            )}
            {/* From a signed quote to an active client. Staff-only by the
                guard above, and for any business with an id: one that has
                never been scanned is exactly who this screen is for. */}
            {screen === "onboarding" && isStaffDrillIn && businessId && showOnboarding && (
                <OnboardingTab business={business} businessId={businessId} />
            )}
            {screen === "connectors" && (
                <DataConnectors
                    connectors={liveConnectorList ?? getConnectors(business)}
                    gscState={gscState}
                    // Only a live business can actually connect anything; a
                    // fixture id would 404 the endpoint.
                    businessId={!isFixtureBusiness && businessId ? businessId : undefined}
                    // Detail too, not just the catalog — a connector change (and
                    // especially an on-demand sync) moves the search numbers,
                    // which ride on the business detail payload.
                    onChanged={refreshLiveData}
                />
            )}
            {/* Staff get the full Tracker (scoreboard + board + timeline + export);
                the client keeps the simple to-do list. Tracker is deeply coupled to
                the fixture-only trackerState store (#3160 — out of scope: wiring the
                real per-business tracker needs its own pass, not a quick swap). */}
            {screen === "todo" && (isStaffDrillIn ? (
                isFixtureBusiness ? (
                    <Tracker
                        snapshot={snapshot}
                        business={business}
                        onOpenQuestions={() => onNavigate("questions")}
                        onOpenIssue={onOpenSiteIssue}
                        onOpenClient={onOpenClient}
                    />
                ) : liveTracker ? (
                    <Tracker
                        snapshot={snapshot}
                        business={business}
                        liveTracker={liveTracker}
                        // A task row names the check it closes and links to it on
                        // the Site Audit tab (#3400); the checks ride on this
                        // assessment payload.
                        auditIssues={liveAudit?.issues}
                        // The Overview's recommendations fold into Fixes (D7),
                        // joined to the tasks that answer them.
                        recommendations={liveAudit?.recommendations}
                        assessmentId={trackerAssessmentId}
                        onOpenQuestions={() => onNavigate("questions")}
                        // Optimistic already applied by the screen; this persists it.
                        // The BE returns the whole tracker back, but re-adapting it
                        // here would fight the local store's own copy — the next
                        // mount re-fetches, which is when the two reconcile.
                        // Staff-only server-side. The response IS the re-dated
                        // plan, so it replaces state directly rather than
                        // triggering a GET that would race the write.
                        onSaveSettings={trackerAssessmentId ? async (patch) => {
                            const next = await patchTrackerSettings(trackerAssessmentId, patch);
                            if (next) setLiveTracker(adaptTracker(next));
                            return !!next;
                        } : undefined}
                        // #4513. The response IS the regenerated plan — a kept
                        // suggestion becomes a real task through the generator,
                        // so replacing the tracker is what makes the row leave
                        // the queue and appear in its week.
                        onDecideSuggestion={trackerAssessmentId ? async (id, decision) => {
                            const res = await decideSuggestion(trackerAssessmentId, id, decision);
                            if (res?.tracker) setLiveTracker(adaptTracker(res.tracker));
                            return !!res;
                        } : undefined}
                        // #3512: the manual re-read, apart from the one that
                        // follows a write below.
                        onRefreshRecommendations={refreshLiveData}
                        // #3512: was fire-and-forget, so a status set here never
                        // reached the Recommendations cards on Overview — the two
                        // surfaces stated different things about the same task until
                        // a browser reload. Await the write, then re-derive: the
                        // recommendation's workState is computed server-side on read,
                        // so only a refetch can move it.
                        //
                        // Reports the outcome by RETURNING it rather than showing a
                        // snackbar here: SnackbarProvider is mounted inside the
                        // layout OuraShell picks, which this component renders, so
                        // a useSnackbar() call at this level sits above its own
                        // provider and throws. Both layouts mount it, so the shell
                        // swap does not change this. Tracker owns the message — it
                        // is inside the provider and already reports its actions.
                        onPersist={async (taskId, patch) => {
                            try {
                                await patchTrackerTask(taskId, patch);
                                refreshLiveData();
                                return true;
                            } catch {
                                catchErrorLog(`[oura] tracker task update failed for ${taskId}`);
                                return false;
                            }
                        }}
                        // Staff-only server-side; a client's call is refused there
                        // rather than the button being hidden on trust here.
                        onRebuild={trackerAssessmentId ? async () => {
                            const res = await rebuildTracker(trackerAssessmentId);
                            // Replace the whole tracker rather than re-fetching:
                            // the rebuild response IS the new plan, and a second
                            // GET would race the write that just happened.
                            if (res?.tracker) setLiveTracker(adaptTracker(res.tracker));
                            return res;
                        } : undefined}
                        // The other half of the loop: a tracker row names the
                        // finding it closes and opens it on the Site Audit tab.
                        onOpenIssue={onOpenSiteIssue}
                        onOpenClient={onOpenClient}
                    />
                ) : trackerFailed ? (
                    <PlanUnreadable recommendations={liveAudit?.recommendations ?? []} onOpenIssue={onOpenSiteIssue} />
                ) : (
                    <Card>
                        <CardBody>
                            <div className="flex justify-center py-16">
                                <Loader2Icon className="h-5 w-5 animate-spin text-gray-400" />
                            </div>
                        </CardBody>
                    </Card>
                )
            ) : (
                <ToDo snapshot={snapshot} business={business} />
            ))}
        </>
    );

    /**
     * A run that finished PARTIAL still fills every screen with numbers, so
     * without this the fact that the engine never ran is invisible: the scores
     * just look low rather than unmeasured. The backend has been sending the
     * reason all along; it was only ever rendered when there were no results
     * at all, which is the one case where the operator already knew something
     * was wrong. In the staff chrome it sits in the content column, over the
     * screen it qualifies, not across the top of the sidebar.
     */
    const partialNotice = !isFixtureBusiness && detail?.latest_assessment
        && detail.latest_assessment.status !== "complete"
        && !!detail.latest_assessment.error_code ? (
            <GatedReason
                inline
                errorCode={detail.latest_assessment.error_code}
                errorDetail={detail.latest_assessment.error_detail}
                // The server's own sentence for a partial scan (BE-3); it wins
                // over the code map inside the component.
                notice={detail.latest_assessment.notice_detail}
                stage={detail.latest_assessment.stage}
                status={detail.latest_assessment.status}
            />
        ) : null;

    // A partial scan's note is never above the merchant's screens (redesign):
    // Visibility says it under its tiles (Visibility.tsx), the other screens
    // do without it. A failed run's notice still shows.
    const noticeInScreen = MERCHANT_V2 && role !== "oura-staff"
        && detail?.latest_assessment?.status === "partial";

    return (
        <OuraShell>
            {!isStaffDrillIn && !noticeInScreen && partialNotice}

            {/* Drawer provider is keyed to the business so it resets on switch */}
            <ActionDrawerProvider key={businessId} snapshot={snapshot}>
                {isStaffDrillIn ? (
                    <BusinessDetail
                        business={business}
                        snapshot={snapshot}
                        active={screen as DetailTab}
                        onNavigate={onNavigate}
                        onOpenReport={onOpenReport}
                        onRenamed={isFixtureBusiness ? undefined : refreshLiveData}
                        // A past scan cannot be rerun or renamed FROM — those
                        // act on the business as it is now, and offering them
                        // beside figures from three weeks ago invites the one
                        // mistake this feature has to prevent (#3933).
                        readOnly={viewingPast}
                        scanPicker={scanPicker}
                        hubFacts={hubFacts}
                        rescanInfo={rescanInfo}
                    >
                        {partialNotice}
                        {scanBanner}
                        {inner}
                    </BusinessDetail>
                ) : (
                    <>
                        {scanBanner}
                        {inner}
                    </>
                )}
            </ActionDrawerProvider>
        </OuraShell>
    );
}
